import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import WarningIcon from '@mui/icons-material/Warning';
import Stack from '@mui/material/Stack';
import { CircularProgress } from '@mui/material';

import { useDataset } from '../services/api/KeynomeAPIHooks.js';
import { useAnalyzeClonality } from '../services/api/KeynomeAPIMutations.js';
import StatusIcon from '../Icons/StatusIcon.jsx';

const MAX_ALLOWED_NUM_SAMPLES = 10;

/**
 * A button to trigger a clonality analysis for the samples in a dataset, or a status icon if 
 * there is already an existing clonality analysis for the component.
 *
 * This component will go in the add-samples dialog, and will trigger a clonality analysis on the 
 * backend when clicked. It will be disabled if the dataset has more than 10 samples. If a clonality 
 * analysis is already running, then the button will instead be replaced by a "Analyzing" status 
 * icon, or "Failed" if the current analysis has failed. If there already is a complete analysis,  
 * then it will be replaced by a "Succeeded" icon.
 */
export default function TriggerClonalityAnalysisButtonOrStatusIcon(props) {
  const { activeRun, samples } = props;
  const { organization, setUserErrorMessage, setUserSuccessMessage } = useOutletContext();
  // Refetch the sequencing_run record with the clonality status
  const { data: run } = useDataset(
    activeRun?.id, 
    { 
      queryParams: { extraFields: "status,clonality_analysis_status" }, 
      // Poll the clonality status every 20 seconds to keep the indicator up to date
      useQueryOptions: { refetchInterval: 20_000 } 
    }
  )
  const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false);
  const { 
    mutateAsync: analyzeClonality, 
    isPending: analyzeClonalityIsPending 
  } = useAnalyzeClonality(
    { 
      onSuccessFn: () => {
        setUserSuccessMessage("Started clonality analysis! Please monitor the dataset for the progress.")
      },
      onErrorFn: () => {
        setUserErrorMessage("Failed to start a clonality analysis! Contact support@dayzerodiagnostics.com if you require assistance.")
      },
    }
  );

  // Clonality analysis is current only for internal use
  if (!organization?.attributes?.metadata?.FF_clonality) {
    return null
  }

  // Clonality analysis is only for Illumina
  const platform = run?.attributes?.platform || run?.attributes?.sequencing_platform;
  if (platform !== "illumina") {
    return null
  }

  // Replace the button with a status indicator if there is already an existing analysis execution
  const clonality_analysis_status = run?.attributes?.clonality_analysis_status;
  if (clonality_analysis_status !== "not_started") {
    return (<StatusIcon status={clonality_analysis_status} />)
  } 

  // Determine if the button should be disabled and what message to show as an explanation for the 
  // disabling.
  let buttonIsEnabled = true;
  let disabledButtonHoverText = "";
  // Case: there are no samples in the dataset yet
  if (samples?.length === 0) {
    buttonIsEnabled = false;
    disabledButtonHoverText = "Please upload samples and wait for analyses to complete before detecting clonal samples";
  // Case: there are too many samples in the dataset
  } else if (samples?.length > MAX_ALLOWED_NUM_SAMPLES) {
    buttonIsEnabled = false;
    disabledButtonHoverText = `Clonality detection only is available for datasets with up to ${MAX_ALLOWED_NUM_SAMPLES} samples`;
  // Case: not all samples were successfully analyzed yet
  } else if (run?.attributes?.status !== "succeeded") {
    buttonIsEnabled = false;
    disabledButtonHoverText = "Clonality detection can only be run on successfully analyzed samples";
  } 

  const button = (
    <Button 
      disabled={!buttonIsEnabled}
      variant="contained" 
      aria-label="detect-clonal-samples" 
      style={buttonIsEnabled ? { color: "white", backgroundColor: "#0081ff" } : {}}
      onClick={ () => { setConfirmDialogIsOpen(true) } } 
    >
      Detect Clonal Samples
    </Button>
  );

  const confirmationDialog = (
    <Dialog open={confirmDialogIsOpen}>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <WarningIcon fontSize="medium" color='primary' />
          Clonality detection will lock this dataset!
        </Stack>
      </DialogTitle>
      <DialogContent>
        Starting clonality detection will lock this dataset, preventing further upload! Please make sure you are finished uploading samples to the dataset before continuing.
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDialogIsOpen(false)} variant="contained">Cancel</Button>
        { analyzeClonalityIsPending ? 
          <CircularProgress sx={{ color: 'orange', ml: 4 }} /> :
          <Button onClick={
            async () => {
              try {
                await analyzeClonality({ runId: run?.id });
              } catch (error) {
                console.log("Failed to trigger clonality analysis! Encoutered error: ", error)
                setUserErrorMessage("Failed to start a clonality analysis! Contact support@dayzerodiagnostics.com if you require assistance.") 
              } finally {
                setConfirmDialogIsOpen(false);
              }
            }
          }>Confirm</Button>
        }
      </DialogActions>
    </Dialog>
  );

  // If the button is enabled...
  if (buttonIsEnabled) {
    // ...then return the enabled button and confirmation dialog...
    return (
      <>
        {button}
        {confirmationDialog}
      </>
    ) 
  // ...otherwise...
  } else {
    // ...return the disabled button wrapped in a tooltip with the disable-explanation
    return (
      <Tooltip title={disabledButtonHoverText}>
        <span>
          {button}
        </span>
      </Tooltip>
    )
  }
}
